import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import copyIcon from '@/assets/icons/copy.svg';
import doneIcon from '@/assets/icons/done.svg';
import styles from './DonationsAdmin.module.css';

interface Props {
  fundId: number;
  /** Renders without the card background/border, for pages that provide their own framing. */
  frameless?: boolean;
  /** Only the four copy chips, in one row - for a document row that already has a title (HAT-1231). */
  inline?: boolean;
}

const VARIANTS = [
  { lang: 'heb', currency: 'ILS' },
  { lang: 'en', currency: 'ILS' },
  { lang: 'heb', currency: 'USD' },
  { lang: 'en', currency: 'USD' },
];

const ShareableLinks = ({ fundId, frameless = false, inline = false }: Props) => {
  const { t } = useTranslation();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const links = useMemo(
    () =>
      VARIANTS.map((v) => ({
        ...v,
        key: `${v.lang}-${v.currency}`,
        url: `${window.location.origin}/new/donate/${fundId}?lang=${v.lang}&currency=${v.currency}`,
      })),
    [fundId]
  );

  const copy = async (key: string, url: string) => {
    await navigator.clipboard.writeText(url);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const chips = links.map((l) => (
    <button
      key={l.key}
      type="button"
      className={`${styles.linkCopyBtn} ${inline ? styles.linkCopyBtnInline : ''}`.trim()}
      onClick={() => copy(l.key, l.url)}
    >
      <span className={styles.linkCopyLabel}>
        {t(`donations.admin.lang.${l.lang}`)} / {l.currency === 'USD' ? '$' : '₪'}
      </span>
      <img
        src={copiedKey === l.key ? doneIcon : copyIcon}
        alt={copiedKey === l.key ? t('donations.admin.copied') : t('donations.admin.copy')}
        className={styles.linkCopyIcon}
      />
    </button>
  ));

  if (inline) return <span className={styles.linkChips}>{chips}</span>;

  return (
    <div className={`${styles.panel} ${frameless ? styles.panelFrameless : ''}`.trim()}>
      <h2 className={styles.panelTitle}>{t('donations.admin.shareLinksTitle')}</h2>
      <p className={styles.panelSubtitle}>{t('donations.admin.shareLinksSubtitle')}</p>
      <div className={styles.linkGrid}>{chips}</div>
    </div>
  );
};

export default ShareableLinks;
